import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Card,
    Row,
    Col,
    Tooltip,
    Select,
    Switch,
    InputNumber,
    Tag,
    Pagination
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';
import {
    AppVersion,
    AppVersionListRequest,
    CreateAppVersionRequest,
    UpdateAppVersionRequest,
    App,
    AppListRequest
} from '../types';

const { Option } = Select;
const { TextArea } = Input;

const AppVersionManagement: React.FC = () => {
    const [appVersions, setAppVersions] = useState<AppVersion[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total: number, range: [number, number]) =>
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
    });

    // 筛选条件
    const [filters, setFilters] = useState({
        app_tag: '',
        os: ''
    });

    // 模态框状态
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingAppVersion, setEditingAppVersion] = useState<AppVersion | null>(null);
    const [form] = Form.useForm();

    // 应用列表
    const [apps, setApps] = useState<App[]>([]);

    // 获取应用列表
    const fetchApps = async () => {
        try {
            const request: AppListRequest = {
                page: 1,
                page_size: 100 // 获取所有应用标签
            };
            const response = await ApiService.getAppList(request);
            if (response.code === 200) {
                setApps(response.data.apps);
            }
        } catch (error) {
            console.error('获取应用列表失败:', error);
        }
    };

    // 获取应用版本列表
    const fetchAppVersions = async (page: number = pagination.current) => {
        setLoading(true);
        try {
            const request: AppVersionListRequest = {
                page,
                page_size: pagination.pageSize,
                ...filters
            };

            // 过滤空值
            if (!request.app_tag) delete request.app_tag;
            if (!request.os) delete request.os;

            const response = await ApiService.getAppVersionList(request);
            if (response.code === 200) {
                setAppVersions(response.data.app_versions);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    total: response.data.pagination.total
                }));
            } else {
                message.error(response.message || '获取应用版本列表失败');
            }
        } catch (error) {
            console.error('获取应用版本列表失败:', error);
            message.error('获取应用版本列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 页面加载时获取数据
    useEffect(() => {
        fetchApps();
        fetchAppVersions(1);
    }, []);

    // 当筛选条件变化时自动触发筛选
    useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchAppVersions(1);
    }, [filters.app_tag, filters.os]);



    // 处理新增应用版本
    const handleAdd = () => {
        setEditingAppVersion(null);
        setIsModalVisible(true);
        form.resetFields();
    };

    // 处理编辑应用版本
    const handleEdit = (appVersion: AppVersion) => {
        setEditingAppVersion(appVersion);
        setIsModalVisible(true);
        form.setFieldsValue({
            version: appVersion.version,
            app_tag: appVersion.app_tag,
            app_build: appVersion.app_build,
            os: appVersion.os,
            download_url: appVersion.download_url,
            force_update: appVersion.force_update,
            update_notes: appVersion.update_notes,
            is_latest: appVersion.is_latest
        });
    };

    // 处理删除应用版本
    const handleDelete = async (id: number) => {
        try {
            const response = await ApiService.deleteAppVersion({ id });
            if (response.code === 200) {
                message.success(response.message || '应用版本删除成功');
                fetchAppVersions(pagination.current);
            } else {
                message.error(response.message || '应用版本删除失败');
            }
        } catch (error) {
            console.error('删除应用版本失败:', error);
            message.error('删除应用版本失败');
        }
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (editingAppVersion) {
                // 更新应用版本
                const updateRequest: UpdateAppVersionRequest = {
                    id: editingAppVersion.id,
                    ...values
                };
                const response = await ApiService.updateAppVersion(updateRequest);
                if (response.code === 200) {
                    message.success(response.message || '应用版本更新成功');
                    setIsModalVisible(false);
                    fetchAppVersions(pagination.current);
                } else {
                    message.error(response.message || '应用版本更新失败');
                }
            } else {
                // 创建应用版本
                const createRequest: CreateAppVersionRequest = values;
                const response = await ApiService.createAppVersion(createRequest);
                if (response.code === 200) {
                    message.success(response.message || '应用版本创建成功');
                    setIsModalVisible(false);
                    fetchAppVersions(pagination.current);
                } else {
                    message.error(response.message || '应用版本创建失败');
                }
            }
        } catch (error) {
            console.error('操作失败:', error);
            message.error('操作失败');
        }
    };

    // 处理模态框取消
    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // 处理分页变化
    const handlePaginationChange = (page: number, pageSize?: number) => {
        const newPagination = {
            ...pagination,
            current: page,
            pageSize: pageSize || pagination.pageSize
        };
        setPagination(newPagination);
        fetchAppVersions(page);
    };

    // 表格列定义
    const columns: ColumnsType<AppVersion> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: '版本号',
            dataIndex: 'version',
            key: 'version',
            width: 120,
        },
        {
            title: '构建号',
            dataIndex: 'app_build',
            key: 'app_build',
            width: 100,
        },
        {
            title: '应用标签',
            dataIndex: 'app_tag',
            key: 'app_tag',
            width: 120,
        },
        {
            title: '操作系统',
            dataIndex: 'os',
            key: 'os',
            width: 100,
        },
        {
            title: '下载地址',
            dataIndex: 'download_url',
            key: 'download_url',
            ellipsis: {
                showTitle: false,
            },
            render: (url: string) => (
                <Tooltip placement="topLeft" title={url}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                    </a>
                </Tooltip>
            ),
        },

        {
            title: '更新说明',
            dataIndex: 'update_notes',
            key: 'update_notes',
            ellipsis: {
                showTitle: false,
            },
            render: (notes: string) => (
                <Tooltip placement="topLeft" title={notes}>
                    {notes}
                </Tooltip>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (time: string) => new Date(time).toLocaleString(),
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="编辑">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除">
                        <Popconfirm
                            title="确定要删除这个应用版本吗？"
                            onConfirm={() => handleDelete(record.id)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card title="应用版本管理" style={{ marginBottom: '24px' }}>
                {/* 筛选区域 */}
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={6}>

                        <Select
                            placeholder="请选择应用标签"
                            value={filters.app_tag || undefined}
                            onChange={(value) => setFilters(prev => ({ ...prev, app_tag: value || '' }))}
                            style={{ width: '100%' }}
                            allowClear>
                            {apps.map(app => (
                                <Option key={app.id} value={app.app_tag}>
                                    {app.app_tag}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            placeholder="操作系统"
                            value={filters.os || undefined}
                            onChange={(value) => setFilters(prev => ({ ...prev, os: value || '' }))}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="android">Android</Option>
                            <Option value="androidgoogle">Android Google</Option>
                            <Option value="ios">iOS</Option>
                        </Select>
                    </Col>
                    <Col span={12}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                            >
                                新增应用版本
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchAppVersions(pagination.current)}
                            >
                                刷新
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* 表格 */}
                <Table
                    columns={columns}
                    dataSource={appVersions}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 1200 }}
                />

                {/* 分页 */}
                <div style={{ marginTop: '16px', textAlign: 'right' }}>
                    <Pagination
                        current={pagination.current}
                        total={pagination.total}
                        pageSize={pagination.pageSize}
                        showSizeChanger={pagination.showSizeChanger}
                        showQuickJumper={pagination.showQuickJumper}
                        showTotal={pagination.showTotal}
                        onChange={handlePaginationChange}
                        onShowSizeChange={handlePaginationChange}
                    />
                </div>
            </Card>

            {/* 新增/编辑模态框 */}
            <Modal
                title={editingAppVersion ? '编辑应用版本' : '新增应用版本'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={600}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        force_update: false,
                        is_latest: false
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="版本号"
                                name="version"
                                rules={[
                                    { required: true, message: '请输入版本号' },
                                    { max: 20, message: '版本号不能超过20个字符' }
                                ]}
                            >
                                <Input placeholder="例如: 1.0.0" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="构建号"
                                name="app_build"
                                rules={[
                                    { required: true, message: '请输入构建号' },
                                    { type: 'number', min: 0, message: '构建号必须为非负整数' }
                                ]}
                            >
                                <InputNumber placeholder="例如: 100" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="应用标签"
                                name="app_tag"
                                rules={[
                                    { required: true, message: '请选择应用标签' }
                                ]}
                            >
                                <Select placeholder="请选择应用标签">
                                    {apps.map(app => (
                                        <Option key={app.id} value={app.app_tag}>
                                            {app.app_tag}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="操作系统"
                                name="os"
                                rules={[
                                    { required: true, message: '请选择操作系统' }
                                ]}
                            >
                                <Select placeholder="请选择操作系统">
                                    <Option value="android">Android</Option>
                                    <Option value="androidgoogle">Android Google</Option>
                                    <Option value="ios">iOS</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="下载地址"
                        name="download_url"
                        rules={[
                            { required: true, message: '请输入下载地址' },
                            { type: 'url', message: '请输入有效的URL' },
                            { max: 500, message: '下载地址不能超过500个字符' }
                        ]}
                    >
                        <Input placeholder="https://example.com/app.apk" />
                    </Form.Item>

                    <Form.Item
                        label="更新说明"
                        name="update_notes"
                    >
                        <TextArea
                            rows={4}
                            placeholder="请输入版本更新说明..."
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="强制更新"
                                name="force_update"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="最新版本"
                                name="is_latest"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default AppVersionManagement;